import { Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';

const modes = [
  {
    key: 'hd',
    icon: 'ri-4k-line',
    title: '高清原图模式',
    tag: '画质优先',
    desc: '漫蛙漫画网为连载作品保留最高 4K 的无损原图，开启高清原图模式后，系统会根据屏幕分辨率智能匹配清晰度档位，即使放大到全屏逐格细看，线条、网点与灰阶层次依然锐利分明。',
    points: ['支持 1080P / 2K / 4K 多档位', '按屏幕自动匹配最合适画质', '放大浏览不损失细节'],
    img: 'https://readdy.ai/api/search-image?query=close%20up%20detail%20of%20manga%20ink%20artwork%20with%20crisp%20fine%20lines%20and%20screen%20tones%2C%20magnified%20comic%20panel%2C%20sharp%20high%20detail%20scan%2C%20warm%20amber%20accent%20lighting%2C%20dark%20background%2C%20editorial%20style%2C%20professional%20scan%20quality&width=1200&height=800&seq=mode-hd&orientation=landscape',
  },
  {
    key: 'double',
    icon: 'ri-book-open-line',
    title: '双页浏览模式',
    tag: '沉浸翻页',
    desc: '面向横屏平板与桌面设备优化，左右双页无缝拼接，模拟实体单行本的翻页手感，配合流畅的翻页过渡与书脊中线，带来接近纸书的沉浸阅读体验。',
    points: ['模拟实体单行本翻页', '书脊中线自动对齐', '横屏平板体验最佳'],
    img: 'https://readdy.ai/api/search-image?query=open%20manga%20tankoubon%20book%20showing%20two%20spread%20pages%20with%20a%20spine%20line%2C%20flat%20lay%20photography%20style%2C%20warm%20desk%20lighting%2C%20comic%20pages%20detail%2C%20cozy%20reading%20scene%2C%20warm%20amber%20tones%2C%20high%20detail&width=1200&height=800&seq=mode-double&orientation=landscape',
  },
  {
    key: 'night',
    icon: 'ri-moon-clear-line',
    title: '夜间模式',
    tag: '护眼追更',
    desc: '自适应环境亮度的柔和深色界面，支持色温微调与亮度跟随，深夜追更时减少屏幕眩光，保护视力，同时保留画面的层次与明暗关系。',
    points: ['跟随系统自动切换', '独立亮度与色温调节', '深色界面降低眩光'],
    img: 'https://readdy.ai/api/search-image?query=dark%20cozy%20night%20scene%20of%20someone%20reading%20a%20tablet%20in%20bed%20under%20warm%20dim%20lamp%20light%2C%20night%20mode%20dark%20ui%20glow%2C%20moody%20ambient%20lighting%2C%20deep%20charcoal%20and%20amber%20tones%2C%20relaxed%20atmosphere%2C%20cinematic%20photography&width=1200&height=800&seq=mode-night&orientation=landscape',
  },
  {
    key: 'zoom',
    icon: 'ri-zoom-in-line',
    title: '缩放设置',
    tag: '细节掌控',
    desc: '支持多点触控与滑杆两种缩放方式，可精确框选想要放大的分镜、对话框与细节画面，聚焦作者精心刻画的每一处笔触。',
    points: ['多点触控缩放', '滑杆精确控制比例', '聚焦对话与分镜细节'],
    img: 'https://readdy.ai/api/search-image?query=macro%20view%20of%20fingers%20zooming%20into%20a%20tablet%20screen%20showing%20a%20comic%20panel%20detail%2C%20interactive%20zoom%20concept%2C%20warm%20orange%20accent%20lighting%2C%20dark%20background%2C%20close%20up%2C%20modern%20digital%20device%2C%20high%20detail&width=1200&height=800&seq=mode-zoom&orientation=landscape',
  },
  {
    key: 'scroll',
    icon: 'ri-arrow-down-double-line',
    title: '滚动阅读模式',
    tag: '一划到底',
    desc: '纵轴连续滚动式阅读，适合长条漫与手机竖屏场景，横竖屏随心切换，翻页不再打断沉浸节奏，拇指轻轻滑动即可一划到底。',
    points: ['纵轴连续滚动', '横竖屏自由切换', '长条漫专属适配'],
    img: 'https://readdy.ai/api/search-image?query=vertical%20scrolling%20comic%20strip%20on%20a%20smartphone%20held%20in%20one%20hand%2C%20long%20vertical%20webtoon%20panels%2C%20warm%20amber%20lighting%2C%20dark%20moody%20background%2C%20close%20up%2C%20modern%20digital%20reading%2C%20high%20detail&width=1200&height=800&seq=mode-scroll&orientation=landscape',
  },
];

export default function ReadingModes() {
  useSeo({
    title: '漫蛙漫画网阅读模式 - 高清原图、双页浏览、夜间模式与滚动阅读',
    description: '了解漫蛙漫画网的多种阅读模式：高清原图、双页浏览、夜间模式、缩放设置与滚动阅读，针对不同设备与场景优化漫画阅读体验。',
    canonicalPath: '/reading-modes',
  });
  return (
    <div className="bg-background-950 text-background-100">
      {/* Hero */}
      <section className="relative overflow-hidden bg-background-950">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20immersive%20reading%20interface%20mockup%20showing%20multiple%20manga%20reading%20modes%20on%20devices%2C%20glowing%20amber%20accents%2C%20deep%20charcoal%20background%2C%20floating%20comic%20panels%2C%20modern%20ui%20concept%2C%20warm%20orange%20and%20black%20palette%2C%20dramatic%20lighting%2C%20high%20detail%20digital%20art&width=1600&height=900&seq=mode-hero&orientation=landscape"
          alt="漫蛙漫画网阅读模式界面展示"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-background-950/40 to-background-950"></div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 py-28 md:px-6 md:py-36">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/20 px-4 py-1.5 text-sm font-medium text-primary-300 ring-1 ring-primary-400/40">
              <i className="ri-settings-5-line"></i>
              阅读模式全解析
            </span>
            <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              五种模式，读懂每一格画面
            </h1>
            <p className="mt-5 mx-auto max-w-2xl text-base leading-relaxed text-background-300 md:text-lg">
              高清原图、双页浏览、夜间模式、缩放设置与滚动阅读，针对不同作品、设备与阅读场景量身优化，把体验做到极致。
            </p>
          </div>
        </div>
      </section>

      {/* Modes detail */}
      <section className="bg-background-950">
        <div className="mx-auto max-w-7xl space-y-16 px-4 py-16 md:px-6 md:py-24">
          {modes.map((m, i) => (
            <article
              key={m.key}
              id={m.key}
              className={`grid items-center gap-10 lg:grid-cols-12 ${i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''}`}
            >
              <div className="lg:col-span-6">
                <div className="overflow-hidden rounded-lg border border-background-800">
                  <img
                    src={m.img}
                    alt={`${m.title}演示图`}
                    className="h-auto w-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:col-span-6">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                  <i className={m.icon}></i>
                  {m.tag}
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold text-background-50 md:text-3xl">{m.title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-background-300 md:text-base">{m.desc}</p>
                <ul className="mt-6 space-y-3">
                  {m.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-sm text-background-200">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-background-50">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-background-800 bg-background-950">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6">
          <div className="flex flex-col items-center gap-6 rounded-2xl border border-background-800 bg-background-900 px-8 py-12 text-center">
            <i className="ri-book-open-line text-4xl text-primary-500"></i>
            <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
              选一部喜欢的漫画，亲自体验
            </h2>
            <p className="max-w-xl text-sm text-background-300 md:text-base">
              在漫画库里任意选择作品，随时切换阅读模式，感受高清原图与夜间模式带来的舒适体验。
            </p>
            <Link
              to="/comics"
              className="whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
            >
              前往漫画库
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}